import { forwardRef, useMemo } from 'react'
import { format } from 'echarts'
import ReactECharts from 'echarts-for-react'
import { LOSS_CURVE_COLOUR, LOSS_CURVE_REFERENCE_COLOUR } from '../constants/riskProfile.constants'
import type { RiskProfileAsset } from '../types/riskProfileAssets.types'
import { resolveAssetLabel, resolveLabelKey } from '../utils/riskProfileAsset.utils'

export type LossCurveMode = 'damage_cost' | 'quantity'

export interface RiskProfileLossCurveChartProps {
  data: RiskProfileAsset[]
  mode: LossCurveMode
}

interface LorenzPoint {
  assetId: string
  cumulativeAssetsPct: number
  cumulativeMetricPct: number
}

const MODE_LABELS: Record<LossCurveMode, { xAxis: string; yAxis: string; tooltipY: string }> = {
  damage_cost: {
    xAxis: 'Cumulative share of assets',
    yAxis: 'Cumulative damage cost',
    tooltipY: 'Cumulative damage cost',
  },
  quantity: {
    xAxis: 'Cumulative share of assets',
    yAxis: 'Cumulative damaged quantity',
    tooltipY: 'Cumulative damaged quantity',
  },
}

export function buildLorenzPoints(assets: RiskProfileAsset[], mode: LossCurveMode): LorenzPoint[] {
  if (assets.length === 0) return []

  const yValue = (a: RiskProfileAsset) =>
    mode === 'damage_cost' ? a.damage_cost : a.exposed_quantity
  const xValue = (a: RiskProfileAsset) => (mode === 'damage_cost' ? a.asset_metric : 1)

  const sorted = [...assets].sort((a, b) => yValue(b) - yValue(a))
  const totalY = sorted.reduce((sum, a) => sum + yValue(a), 0)
  const totalX = sorted.reduce((sum, a) => sum + xValue(a), 0)
  if (totalY <= 0 || totalX <= 0) return []

  const labelKey = resolveLabelKey(sorted[0])
  let cumulativeX = 0
  let cumulativeY = 0
  const points: LorenzPoint[] = [{ assetId: '', cumulativeAssetsPct: 0, cumulativeMetricPct: 0 }]
  for (const asset of sorted) {
    cumulativeX += xValue(asset)
    cumulativeY += yValue(asset)
    points.push({
      assetId: resolveAssetLabel(asset, labelKey),
      cumulativeAssetsPct: (cumulativeX / totalX) * 100,
      cumulativeMetricPct: (cumulativeY / totalY) * 100,
    })
  }
  return points
}

const RiskProfileLossCurveChart = forwardRef<ReactECharts, RiskProfileLossCurveChartProps>(
  ({ data, mode }, ref) => {
    const points = useMemo(() => buildLorenzPoints(data, mode), [data, mode])
    const labels = MODE_LABELS[mode]

    const option = useMemo(
      () => ({
        grid: { left: 64, right: 32, top: 24, bottom: 56 },
        tooltip: {
          trigger: 'axis',
          formatter: (
            params: Array<{
              seriesName: string
              data: { value: [number, number]; assetId?: string }
            }>,
          ) => {
            const point = params.find((p) => p.seriesName === 'Loss curve')
            if (!point) return ''
            const [x, y] = point.data.value
            const title = point.data.assetId
              ? `<div style="font-weight:600;margin-bottom:4px">${format.encodeHTML(point.data.assetId)}</div>`
              : ''
            return (
              `${title}` +
              `<div>${labels.xAxis}: <span style="font-weight:600">${x.toFixed(1)}%</span></div>` +
              `<div>${labels.tooltipY}: <span style="font-weight:600">${y.toFixed(1)}%</span></div>`
            )
          },
        },
        xAxis: {
          type: 'value',
          min: 0,
          max: 100,
          name: labels.xAxis,
          nameLocation: 'middle',
          nameGap: 32,
        },
        yAxis: {
          type: 'value',
          min: 0,
          max: 100,
          name: labels.yAxis,
          nameLocation: 'middle',
          nameGap: 44,
        },
        series: [
          {
            name: 'Equality',
            type: 'line',
            data: [
              [0, 0],
              [100, 100],
            ],
            showSymbol: false,
            lineStyle: { color: LOSS_CURVE_REFERENCE_COLOUR, type: 'dashed' },
            silent: true,
          },
          {
            name: 'Loss curve',
            type: 'line',
            smooth: true,
            showSymbol: true,
            symbol: 'circle',
            symbolSize: 5,
            data: points.map((p) => ({
              value: [p.cumulativeAssetsPct, p.cumulativeMetricPct],
              assetId: p.assetId,
            })),
            lineStyle: { color: LOSS_CURVE_COLOUR, width: 3 },
            itemStyle: {
              color: LOSS_CURVE_COLOUR,
              borderColor: LOSS_CURVE_COLOUR,
              borderWidth: 0,
            },
          },
        ],
      }),
      [points, labels],
    )

    if (points.length === 0) {
      return (
        <p className="text-sm text-muted-foreground">No comparable assets for this selection.</p>
      )
    }

    return <ReactECharts ref={ref} option={option} notMerge style={{ height: 380 }} />
  },
)

RiskProfileLossCurveChart.displayName = 'RiskProfileLossCurveChart'

export default RiskProfileLossCurveChart
